/* ---------------------------------------------------------------------------
   Excel's ROUND, because the reference values were produced by it.

   WHY NOT Math.round. Two differences, and both bite.

   HALF AWAY FROM ZERO, not half up. Excel rounds -2.5 to -3; JavaScript's
   Math.round(-2.5) is -2. Negative moments are ordinary in this program — the
   continuous workbooks rate the pier section on negative moment throughout — so
   this is not a theoretical concern.

   DIGITS, not just integers. `ROUND(x, 2)` is used at nearly every point in the
   program where a value is carried forward, and the naive `Math.round(x*100)/100`
   is wrong for values that float-point cannot hold: 1.005 * 100 is
   100.49999999999999, so the naive form gives 1.0 where Excel gives 1.01.

   The fix for the second is to round the DECIMAL representation rather than the
   binary float. `toFixed` is not that fix, and this is worth being precise
   about: `(1.005).toFixed(2)` is "1.00", because it faithfully rounds the double
   nearest 1.005, which is 1.00499999999999989. Excel gives 1.01, because it
   rounds the number as written. So the digits are taken from JavaScript's own
   shortest round-tripping form — `String(1.005)` is "1.005", the same characters
   Excel would show — and the rounding is done on those digits in integer
   arithmetic, which cannot drift.

   Does this matter to CBPLR? Only at the edges. The values the program rounds
   are computed ones — 14.4507025761128, 154.844802 — and an exact decimal tie
   essentially never arises from them. It is implemented properly anyway, because
   the alternative is a helper that is right for the reference pack and wrong for
   the first bridge whose numbers land on a tie.

   ONE PLACE. Every rounding in this library goes through here, so "where does
   the workbook round?" is answerable by grep — which matters, because the answer
   is load-bearing (correction C10) rather than cosmetic.
   --------------------------------------------------------------------------- */

/**
 * Round to `digits` decimal places, half away from zero, as Excel's ROUND does.
 *
 * `digits` of 0 rounds to a whole number; negative digits round to tens,
 * hundreds and so on, which Excel also permits and which nothing in CBPLR uses.
 */
export function excelRound(value: number, digits: number): number {
  if (!Number.isFinite(value)) return value;
  if (digits < 0) {
    const scale = 10 ** -digits;
    return excelRound(value / scale, 0) * scale;
  }
  /* Sign split so the half-away-from-zero rule holds without depending on how
     the runtime treats a negative tie. */
  const sign = value < 0 ? -1 : 1;
  const magnitude = Math.abs(value);
  const places = Math.trunc(digits);

  const text = String(magnitude);
  /* Exponential form — 1e-7, 1.5e+25. Nothing in CBPLR produces one at a scale
     where rounding to two decimals is meaningful, and the digit surgery below
     would have to parse the exponent to be correct. toFixed is right for these:
     the tie case that motivates the decimal path cannot arise. */
  if (text.includes('e') || text.includes('E')) {
    return sign * Number(magnitude.toFixed(Math.min(places, 100)));
  }

  const dot = text.indexOf('.');
  const whole = dot === -1 ? text : text.slice(0, dot);
  const fraction = dot === -1 ? '' : text.slice(dot + 1);
  /* Already shorter than the requested precision: rounding it would be a no-op
     that only risked reintroducing float error. */
  if (fraction.length <= places) return value;

  const kept = fraction.slice(0, places).padEnd(places, '0');
  const dropped = fraction.slice(places);
  /* Half away from zero: the sign is already outside, so "up" is always away. */
  let scaled = BigInt(whole + kept);
  if (dropped.charCodeAt(0) >= 53 /* '5' */) scaled += 1n;

  const digitsOut = scaled.toString().padStart(places + 1, '0');
  const rounded =
    places === 0
      ? digitsOut
      : `${digitsOut.slice(0, digitsOut.length - places)}.${digitsOut.slice(digitsOut.length - places)}`;
  return sign * Number(rounded);
}

/**
 * Two decimals — the rounding the program applies to almost everything it
 * reports, and to the values it carries forward.
 */
export function round2(value: number): number {
  return excelRound(value, 2);
}
